import { useDispatch } from "react-redux";
import { useState } from "react";
import CustomTable from "../common/ui/CustomDataGridTableFePag";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import { GridRenderCellParams } from "@mui/x-data-grid";
import Tooltip from "@mui/material/Tooltip";
import { actionTypes } from "../../store/actionTypes/actionTypes";
import { ADMIN } from "../../constants/admin";
import { Box, IconButton, Chip, Avatar, Switch, Typography } from "@mui/material";
import { UserPermission } from "../common/UserPermission";
import { patchDataFromApi, deleteDataFromApi } from "../../services/api/apiService";
import { RestfulUrls } from "../../constants/restfulUrls";
import { useNavigate } from "react-router-dom";
import { COMMON } from "../../constants/common";

interface UserManagementTableProps {
  users: any[];
  onRefresh: () => void;
}

const UserManagementTable: React.FC<UserManagementTableProps> = ({ users, onRefresh }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [isToggleModalOpen, setIsToggleModalOpen] = useState<boolean>(false);
  const [disableToggleSubmitBtn, setDisableToggleSubmitBtn] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState<any | null>(null);
  
  // Delete modal state
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  const [disableDeleteSubmitBtn, setDisableDeleteSubmitBtn] = useState(false);
  const [selectedDeleteRowData, setSelectedDeleteRowData] = useState<any | null>(null);

  const onEditIconClick = (selectedRow: any) => {
    dispatch({
      type: actionTypes.UPDATE_USER_FORM_OPEN,
      payload: true,
    });
    dispatch({
      type: actionTypes.UPDATE_SELECTED_USER_DATA,
      payload: selectedRow,
    });
    dispatch({
      type: actionTypes.UPDATE_USER_FORM_TYPE,
      payload: ADMIN.MEETING_FORM_TYPE.EDIT,
    });
  };

  const onSelectedToggleIconClick = (selectedRow: any) => {
    setIsToggleModalOpen(true);
    setSelectedRowData(selectedRow);
  };

  // Generate user initials for avatar
  const getInitials = (firstName: string, lastName: string): string => {
    const first = firstName?.charAt(0)?.toUpperCase() || "";
    const last = lastName?.charAt(0)?.toUpperCase() || "";
    return `${first}${last}`;
  };

  // Get avatar color based on name
  const getAvatarColor = (name: string): string => {
    const colors = [
      "#e91e63", // pink
      "#2196f3", // blue
      "#4caf50", // green
      "#ff9800", // orange
      "#9c27b0", // purple
      "#f44336", // red
      "#00bcd4", // cyan
    ];
    const index = name?.charCodeAt(0) % colors.length || 0;
    return colors[index];
  };

  // Get role badge color
  const getRoleColor = (role: string): { bg: string; text: string } => {
    switch (role) {
      case "SUPER_ADMIN":
        return { bg: "#9c27b0", text: "#fff" }; // purple
      case "ADMIN":
        return { bg: "#2196f3", text: "#fff" }; // blue
      case "RAPPORTEUR":
        return { bg: "#ff9800", text: "#fff" }; // orange
      case "VIEWER":
        return { bg: "#e0e0e0", text: "#424242" }; // light gray
      default:
        return { bg: "#e0e0e0", text: "#424242" };
    }
  };

  const handleCloseToggleModal = () => {
    setIsToggleModalOpen(false);
    setSelectedRowData(null);
  };

  const handleToggleUser = async () => {
    setDisableToggleSubmitBtn(true);

    try {
      const url = `${RestfulUrls.BASE_URL}${RestfulUrls.TOGGLE_USER_STATUS}${selectedRowData?.id}/toggle-status`;
      
      await patchDataFromApi<any>(
        url,
        {},
        () => {
          const action = selectedRowData?.isActive ? "deactivated" : "activated";
          dispatch({
            type: COMMON.SHOW_SNACKBAR,
            payload: {
              message: `User ${action} successfully.`,
              severity: COMMON.SNACKBAR_SEVERITY.SUCCESS,
            },
          });
          onRefresh();
          setIsToggleModalOpen(false);
          setDisableToggleSubmitBtn(false);
          setSelectedRowData(null);
        },
        { isAuthRequired: true },
        navigate,
        (error: any) => {
          console.error("Toggle user failed", error);
          dispatch({
            type: COMMON.SHOW_SNACKBAR,
            payload: {
              message: error?.response?.data?.detail || "Failed to toggle user status.",
              severity: COMMON.SNACKBAR_SEVERITY.ERROR,
            },
          });
          setDisableToggleSubmitBtn(false);
        }
      );
    } catch (error: any) {
      console.error("Toggle user failed", error);
      dispatch({
        type: COMMON.SHOW_SNACKBAR,
        payload: {
          message: error?.response?.data?.detail || "Failed to toggle user status.",
          severity: COMMON.SNACKBAR_SEVERITY.ERROR,
        },
      });
      setDisableToggleSubmitBtn(false);
    }
  };

  // Delete user handlers
  const onSelectedDeleteIconClick = (selectedRow: any) => {
    setIsDeleteModalOpen(true);
    setSelectedDeleteRowData(selectedRow);
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setSelectedDeleteRowData(null);
  };

  const handleDeleteUser = async () => {
    setDisableDeleteSubmitBtn(true);

    try {
      const url = `${RestfulUrls.BASE_URL}${RestfulUrls.DELETE_USER}${selectedDeleteRowData?.id}`;
      
      await deleteDataFromApi<any>(
        url,
        null,
        () => {
          dispatch({
            type: COMMON.SHOW_SNACKBAR,
            payload: {
              message: "User deleted successfully.",
              severity: COMMON.SNACKBAR_SEVERITY.SUCCESS,
            },
          });
          onRefresh();
          setIsDeleteModalOpen(false);
          setDisableDeleteSubmitBtn(false);
          setSelectedDeleteRowData(null);
        },
        { isAuthRequired: true },
        navigate,
        (error: any) => {
          console.error("Delete user failed", error);
          dispatch({
            type: COMMON.SHOW_SNACKBAR,
            payload: {
              message: error?.response?.data?.detail || "Failed to delete user.",
              severity: COMMON.SNACKBAR_SEVERITY.ERROR,
            },
          });
          setDisableDeleteSubmitBtn(false);
        }
      );
    } catch (error: any) {
      console.error("Delete user failed", error);
      dispatch({
        type: COMMON.SHOW_SNACKBAR,
        payload: {
          message: error?.response?.data?.detail || "Failed to delete user.",
          severity: COMMON.SNACKBAR_SEVERITY.ERROR,
        },
      });
      setDisableDeleteSubmitBtn(false);
    }
  };

  const tableHeader = [
    {
      field: "avatar",
      headerName: "",
      width: 55,
      sortable: false,
      align: "center" as const,
      headerAlign: "center" as const,
      renderCell: (params: GridRenderCellParams) => {
        const initials = getInitials(params.row.firstName, params.row.lastName);
        const color = getAvatarColor(params.row.firstName + params.row.lastName);
        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
            <Avatar
              sx={{
                bgcolor: color,
                width: 40,
                height: 40,
                fontSize: "14px",
                fontWeight: 600,
              }}
            >
              {initials}
            </Avatar>
          </Box>
        );
      },
    },
    {
      field: "firstName",
      headerName: "FIRST NAME",
      flex: 0.6,
      minWidth: 120,
      headerClassName: "fw-700",
      align: "center" as const,
      headerAlign: "center" as const,
    },
    {
      field: "lastName",
      headerName: "LAST NAME",
      flex: 0.6,
      minWidth: 120,
      headerClassName: "fw-700",
      align: "center" as const,
      headerAlign: "center" as const,
    },
    {
      field: "email",
      headerName: "EMAIL",
      flex: 1.0,
      minWidth: 180,
      headerClassName: "fw-700",
      align: "center" as const,
      headerAlign: "center" as const,
    },
    {
      field: "role",
      headerName: "ROLE",
      flex: 0.6,
      minWidth: 110,
      headerClassName: "fw-700",
      align: "center" as const,
      headerAlign: "center" as const,
      renderCell: (params: GridRenderCellParams) => {
        const roleColors = getRoleColor(params.row.role);
        const roleLabel = params.row.role === "SUPER_ADMIN" ? "Super Admin" :
                         params.row.role === "ADMIN" ? "Admin" :
                         params.row.role === "RAPPORTEUR" ? "Rapporteur" :
                         params.row.role === "VIEWER" ? "Viewer" : params.row.role;
        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", width: "100%", height: "100%" }}>
            <Chip
              label={roleLabel}
              size="small"
              sx={{
                bgcolor: roleColors.bg,
                color: roleColors.text,
                fontWeight: 500,
                fontSize: "12px",
                height: "24px",
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "isActive",
      headerName: "STATUS",
      flex: 0.6,
      minWidth: 100,
      headerClassName: "fw-700",
      align: "center" as const,
      headerAlign: "center" as const,
      renderCell: (params: GridRenderCellParams) => {
        const isActive = params.row.isActive;
        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, width: "100%", height: "100%" }}>
            <Box
              sx={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                bgcolor: isActive ? "#4caf50" : "#9e9e9e",
              }}
            />
            <Chip
              label={isActive ? "Active" : "Inactive"}
              size="small"
              sx={{
                bgcolor: isActive ? "#e8f5e9" : "#f5f5f5",
                color: isActive ? "#2e7d32" : "#757575",
                fontWeight: 500,
                fontSize: "12px",
                height: "24px",
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "requestReason",
      headerName: "REQUEST REASON",
      flex: 1.0,
      minWidth: 150,
      headerClassName: "fw-700",
      align: "center" as const,
      headerAlign: "center" as const,
      renderCell: (params: GridRenderCellParams) => {
        const requestReason = params.row.requestReason;
        const displayText = requestReason || "N/A";
        const truncatedText = displayText.length > 30 
          ? displayText.substring(0, 30) + "..." 
          : displayText;
        const showTooltip = requestReason && requestReason.length > 30;
        
        const content = (
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              height: "100%",
              width: "100%",
              padding: "8px 0",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: requestReason ? "text.primary" : "text.secondary",
                fontStyle: requestReason ? "normal" : "italic",
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
                textAlign: "center",
              }}
            >
              {truncatedText}
            </Typography>
          </Box>
        );

        return showTooltip ? (
          <Tooltip title={requestReason} arrow placement="top">
            {content}
          </Tooltip>
        ) : (
          content
        );
      },
    },
    {
      field: "pbacAccessAllowed",
      headerName: "PBAC ACCESS ALLOWED",
      flex: 0.7,
      minWidth: 150,
      headerClassName: "fw-700",
      align: "center" as const,
      headerAlign: "center" as const,
      renderCell: (params: GridRenderCellParams) => {
        const pbacAccessAllowed = params.row.pbacAccessAllowed;
        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 0.5, width: "100%", height: "100%" }}>
            <Box
              sx={{
                width: "8px",
                height: "8px",
                borderRadius: "50%",
                bgcolor: pbacAccessAllowed ? "#4caf50" : "#9e9e9e",
              }}
            />
            <Chip
              label={pbacAccessAllowed ? "Allowed" : "Not Allowed"}
              size="small"
              sx={{
                bgcolor: pbacAccessAllowed ? "#e8f5e9" : "#f5f5f5",
                color: pbacAccessAllowed ? "#2e7d32" : "#757575",
                fontWeight: 500,
                fontSize: "12px",
                height: "24px",
              }}
            />
          </Box>
        );
      },
    },
    {
      field: "actions",
      headerName: "ACTIONS",
      flex: 1.0,
      minWidth: 160,
      headerClassName: "fw-700",
      sortable: false,
      cellClassName: "actions-cell",
      align: "center" as const,
      headerAlign: "center" as const,
      renderCell: (params: GridRenderCellParams) => {
        const isActive = params.row.isActive;
        return (
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1, width: "100%", height: "100%" }}>
            <Tooltip title="Edit User">
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  onEditIconClick(params.row);
                }}
                sx={{ 
                  color: "text.secondary",
                  "&:hover": {
                    backgroundColor: "rgba(33, 150, 243, 0.08)",
                    color: "#2196f3",
                  }
                }}
              >
                <EditOutlinedIcon />
              </IconButton>
            </Tooltip>
            <Tooltip title={isActive ? "Deactivate User" : "Activate User"}>
              <Switch
                checked={isActive}
                onChange={(event) => {
                  event.stopPropagation();
                  onSelectedToggleIconClick(params.row);
                }}
                size="small"
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "#2196f3",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "#2196f3",
                  },
                }}
              />
            </Tooltip>
            <Tooltip title="Delete User">
              <IconButton
                size="small"
                onClick={(event) => {
                  event.stopPropagation();
                  onSelectedDeleteIconClick(params.row);
                }}
                sx={{ 
                  color: "#f44336",
                  "&:hover": {
                    backgroundColor: "rgba(244, 67, 54, 0.08)",
                    color: "#d32f2f",
                  }
                }}
              >
                <DeleteOutlineOutlinedIcon />
              </IconButton>
            </Tooltip>
          </Box>
        );
      },
    },
  ];

  // Ensure users is always an array
  const safeUsers = Array.isArray(users) ? users : [];
  
  const rowData = safeUsers.map((user) => ({
    id: user.id,
    firstName: user.firstName || "",
    lastName: user.lastName || "",
    email: user.email || "",
    role: user.role || "",
    isActive: user.isActive !== undefined ? user.isActive : true,
    requestReason: user.requestReason || user.RequestReason || null,
    pbacAccessAllowed: user.pbacAccessAllowed !== undefined ? user.pbacAccessAllowed : false,
    actions: true,
  }));

  return (
    <>
      <CustomTable
        columns={tableHeader}
        rows={rowData}
        hideFooter={false}
        disableColumnFilter={false}
        checkboxSelection={false}
        disableColumnSelector={true}
        disableDensitySelector={true}
        className='extraction_datatable user-management-table'
        showToolbar={false}
        autoHeight={false}
      />
      <UserPermission
        isModalOpen={isToggleModalOpen}
        titleOne={"Are you sure"}
        title={
          selectedRowData?.isActive
            ? "you want to deactivate this user?"
            : "you want to activate this user?"
        }
        disableSubmitBtn={disableToggleSubmitBtn}
        onConfirmedButtonClick={handleToggleUser}
        onCancelButtonClick={handleCloseToggleModal}
        onCloseModal={handleCloseToggleModal}
      />
      <UserPermission
        isModalOpen={isDeleteModalOpen}
        titleOne={"Are you sure"}
        title={"you want to permanently delete this user?"}
        disableSubmitBtn={disableDeleteSubmitBtn}
        onConfirmedButtonClick={handleDeleteUser}
        onCancelButtonClick={handleCloseDeleteModal}
        onCloseModal={handleCloseDeleteModal}
      />
    </>
  );
};

export default UserManagementTable;

